import { useEffect } from 'react';
import type { ReactElement } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
  usePropertyAddressUrl,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YouTubeIcon,
  LinkedInIcon,
  TikTokIcon,
} from '@vibesites/assets';
import type { IconProps } from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy alongside it for use on
// light primary backgrounds. Source SVG mirrors `repli_white.svg` with the
// wordmark's `fill:#fff` swapped to `#1a1a1a` (matches the dark color used
// by the tokens generator's `getContrastTextColor`).
import repliBlackLogoUrl from '../assets/repli_black.svg';

// ============================================================================
// Contrast text color
// ----------------------------------------------------------------------------
// Mirrors `getContrastTextColor` from `@vibesites/tokens` (defined in
// `vendor/vibesites/tokens/generator.ts`, currently module-private and not
// re-exported, so we replicate the exact logic here verbatim — including the
// `0.55` luminance threshold and the `'#ffffff'` / `'#1a1a1a'` return values).
// Using the same function keeps logo color choice (white vs black) in lockstep
// with the auto-computed `--vibe-color-text-on-primary` CSS variable, so the
// footer's text and logos always pick the same contrast side.
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
const TEXT_ON_PRIMARY_DARK = '#1a1a1a';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns the contrasting text color (`'#ffffff'` or `'#1a1a1a'`) for a given
 * background hex. Identical to `getContrastTextColor` in `@vibesites/tokens`.
 * Falls back to the white-on-dark side when the input can't be parsed, which
 * matches how the tokens generator treats unknown / missing primary colors.
 */
function getContrastTextColor(bgHex: string | null | undefined): string {
  if (!bgHex) return TEXT_ON_PRIMARY_LIGHT;
  const luminance = getRelativeLuminance(bgHex);
  // Favor white text on medium tones (gold, tan, dusty blue) where dark text is
  // technically higher-contrast but reads as heavy/muddy. Flip to dark only on
  // genuinely light backgrounds (pastels, cream, near-white).
  return luminance > 0.55 ? TEXT_ON_PRIMARY_DARK : TEXT_ON_PRIMARY_LIGHT;
}

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided entries by canonical day key so we can walk the full
  // week below and fill in any missing day as "Closed".
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!byDay.has(key)) byDay.set(key, entry);
  }

  // Render every day of the week in canonical order (Mon–Sun), plus any
  // non-standard day labels supplied by the data after them. A day absent
  // from the collection is treated as closed rather than dropped.
  const extraKeys = [...byDay.keys()].filter((k) => !DAY_ORDER.includes(k));
  const allKeys = [...DAY_ORDER, ...extraKeys];

  const groups: GroupedHours[] = [];

  for (const key of allKeys) {
    const entry = byDay.get(key);
    const dayLabel = DAY_LABELS[key] || entry?.day || key;
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry?.day || key).slice(0, 3);
    const isClosed = !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the responsive renderer can swap them at the tablet
      // breakpoint without re-deriving the grouping.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// ---- Social links ----
// One slot group per platform. Each is a plain link group (url + hidden
// _target), defaulting to the matching property_*_url field. Icons only
// render when a URL is present, so communities that haven't set a given
// network simply show nothing under "Follow Us".
const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const twitterLinkDef: SlotGroupDefinition = {
  label: 'Twitter / X',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_twitter_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeLinkDef: SlotGroupDefinition = {
  label: 'YouTube',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_youtube_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const linkedinLinkDef: SlotGroupDefinition = {
  label: 'LinkedIn',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_linkedin_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_tiktok_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place/business/address
    // fields). An explicit override from the editor still takes precedence.
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};


// ============================================================================
// Social link
// ----------------------------------------------------------------------------
// Renders a single social icon link, or nothing when no URL is set. Kept as a
// small helper so the six platform blocks in the render stay declarative — the
// `<EditableSlotGroup>` wrappers around each still carry literal groupPaths for
// static schema extraction.
// ============================================================================

function SocialLink({
  url,
  target,
  Icon,
  label,
}: {
  url: string | null | undefined;
  target: string | null | undefined;
  Icon: (props: IconProps) => ReactElement;
  label: string;
}) {
  if (!hasValue(url)) return null;
  return (
    <a
      href={url}
      target={target || '_blank'}
      rel="noopener noreferrer"
      aria-label={label}
      className="text-[var(--vibe-color-text-on-primary)]/70 hover:text-primary-foreground transition-colors"
    >
      <Icon size={20} color="currentColor" aria-label={label} />
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');
  const copyrightName = useContentToken('footer.copyright_name');

  // ---- Contrast-driven logo variant selection ----
  // Decide whether text-on-primary should be white or dark using the same
  // `getContrastTextColor` logic the tokens generator uses to compute
  // `--vibe-color-text-on-primary`. We then pick mono_white / mono_black logo
  // defaults (and the Multihub + Repli marks) to match that decision, so the
  // footer logos always sit on the correct contrast side regardless of how
  // dark/light the property's primary brand color is. The runtime-adjusted
  // slot definitions are passed to both `useSlotGroup` and `EditableSlotGroup`
  // so the editor surfaces the same default the renderer is using.
  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const runtimeLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: {
            ...logoSlotDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  const runtimeManagedByLogoDef: SlotGroupDefinition = useWhiteOnPrimary
    ? managedByLogoDef
    : {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: {
            ...managedByLogoDef.fields.image,
            defaultField: 'property_company_mono_black_logo',
          },
        },
      };

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', runtimeManagedByLogoDef);
  const facebook = useSlotGroup('footer.facebook', facebookLinkDef);
  const instagram = useSlotGroup('footer.instagram', instagramLinkDef);
  const twitter = useSlotGroup('footer.twitter', twitterLinkDef);
  const youtube = useSlotGroup('footer.youtube', youtubeLinkDef);
  const linkedin = useSlotGroup('footer.linkedin', linkedinLinkDef);
  const tiktok = useSlotGroup('footer.tiktok', tiktokLinkDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe Connect / IQ launcher is injected at runtime and sets
  // its own inline `style="z-index: …"` after mount, which beats a stylesheet
  // without `!important`. We only touch the real launcher nodes confirmed in
  // the live DOM — `.rcc-app-cta-wrap`, `.rcc-app-controls`, and
  // `#conversations-webchat-toggle` — and pin them to 120 (just above the open
  // header) with `!important` via `setProperty`. No outermost-fixed-ancestor
  // walk: pinning a non-launcher fixed parent can lift unrelated stacking.
  //
  // The CSS rule in the render is a cheap first pass; this effect is the
  // authoritative fix for post-mount inline overrides. Scoped to Footer
  // because it mounts on every page via DefaultLayout.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const Z = '120';

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!el.matches(SELECTOR)) return;
      pin(el);
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      // querySelectorAll skips the root; check it separately.
      if (root instanceof Element) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only react to attribute changes that could affect identification or
      // stacking — anything else (e.g. inline transforms during animation) is
      // noise we want to ignore.
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      className="text-primary-foreground"
      style={{ backgroundColor: 'var(--vibe-color-primary)' }}
    >

      {/* ---- Main Footer Content ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-center">

          {/* Column 1: Follow Us */}
          {/* Social icons render only when the property has that network set —
              communities without socials simply show the heading alone. Each
              platform keeps a literal-path <EditableSlotGroup> so the editor can
              still surface it once a URL exists. */}
          <div className="flex flex-col items-center gap-5">
            <RichTextContent
              path="footer.follow_us_label"
              defaultValue="<p>Follow Us</p>"
              as="p"
              className="font-body text-[13px] uppercase tracking-[0.2em] text-primary-foreground"
            />
            <div className="flex items-center justify-center gap-4">
              {hasValue(facebook.url) && (
                <EditableSlotGroup groupPath="footer.facebook" definition={facebookLinkDef}>
                  <SocialLink url={facebook.url} target={facebook._target} Icon={FacebookIcon} label="Facebook" />
                </EditableSlotGroup>
              )}
              {hasValue(instagram.url) && (
                <EditableSlotGroup groupPath="footer.instagram" definition={instagramLinkDef}>
                  <SocialLink url={instagram.url} target={instagram._target} Icon={InstagramIcon} label="Instagram" />
                </EditableSlotGroup>
              )}
              {hasValue(twitter.url) && (
                <EditableSlotGroup groupPath="footer.twitter" definition={twitterLinkDef}>
                  <SocialLink url={twitter.url} target={twitter._target} Icon={TwitterIcon} label="Twitter" />
                </EditableSlotGroup>
              )}
              {hasValue(youtube.url) && (
                <EditableSlotGroup groupPath="footer.youtube" definition={youtubeLinkDef}>
                  <SocialLink url={youtube.url} target={youtube._target} Icon={YouTubeIcon} label="YouTube" />
                </EditableSlotGroup>
              )}
              {hasValue(linkedin.url) && (
                <EditableSlotGroup groupPath="footer.linkedin" definition={linkedinLinkDef}>
                  <SocialLink url={linkedin.url} target={linkedin._target} Icon={LinkedInIcon} label="LinkedIn" />
                </EditableSlotGroup>
              )}
              {hasValue(tiktok.url) && (
                <EditableSlotGroup groupPath="footer.tiktok" definition={tiktokLinkDef}>
                  <SocialLink url={tiktok.url} target={tiktok._target} Icon={TikTokIcon} label="TikTok" />
                </EditableSlotGroup>
              )}
            </div>
          </div>

          {/* Column 2: Logo, Contact, Copyright */}
          <div className="flex flex-col items-center text-center gap-4">
            <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-20 w-auto max-w-[220px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-primary-foreground">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {/* Phone */}
            <a
              href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
              className="dniphone dniphonehref font-body text-[15px] text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors"
            >
              <EditableToken
                path="footer.phone"
                defaultField="property_call_tracking_phone"
                defaultValue="(555) 555-5555"
              >
                {formatPhone(phone)}
              </EditableToken>
            </a>

            {/* Address */}
            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressLink.url || propertyAddressUrl || '#'}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="font-body text-[15px] text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors block max-w-[220px]"
              >
                {addressLink.text || '123 Main Street, City, ST 00000'}
              </a>
            </EditableSlotGroup>

            {/* Copyright — community name is editable; the rest is static copy. */}
            <p className="font-body text-[13px] text-[var(--vibe-color-text-on-primary)]/60 mt-1">
              ©{' '}
              <EditableToken
                path="footer.copyright_name"
                defaultField="property_name"
                defaultValue="Our Community"
              >
                {copyrightName || 'Our Community'}
              </EditableToken>
              . All rights reserved.
            </p>
          </div>

          {/* Column 3: Office Hours */}
          <div className="flex flex-col items-center gap-3">
            <RichTextContent
              path="footer.office_hours_label"
              defaultValue="<p>Office Hours</p>"
              as="p"
              className="font-body text-[13px] uppercase tracking-[0.2em] text-primary-foreground"
            />
            {groupedHours.length > 0 ? (
              <dl className="space-y-2 w-full max-w-[280px]">
                {groupedHours.map((group, index) => (
                  <div key={index} className="flex justify-between gap-6 font-body text-sm text-[var(--vibe-color-text-on-primary)]/80">
                    <dt>
                      {/* Full names on mobile and desktop; abbreviated on tablet
                          (md → lg) where the 3-column grid leaves the hours
                          column too narrow for "Monday - Friday". */}
                      <span className="md:hidden lg:inline">{group.label}</span>
                      <span className="hidden md:inline lg:hidden">{group.shortLabel}</span>
                    </dt>
                    <dd className="text-right">{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-body text-sm text-[var(--vibe-color-text-on-primary)]/40">Office hours will be displayed here.</p>
            )}
          </div>

        </div>
      </div>

      {/* ---- Bottom Bar: Credits + Compliance + Legal ---- */}
      {/* Darkened strip (a black overlay over the primary) reproducing the
          two-tone treatment in the design: Multihub on the left, compliance
          icons + legal links centered, Repli on the right. On mobile the three
          grid cells stack and center. The overlay is opacity-based so it works
          on any property primary color rather than being hard-coded to black. */}
      <div className="bg-black/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col items-center gap-5">

            {/* Professionally Managed By — centered above the credit row, shown
                only when a management-company logo is set. */}
            {managedByLogo.image && (
              <EditableSlotGroup
                groupPath="footer.managed_by_logo"
                definition={runtimeManagedByLogoDef}
                className="flex justify-center"
              >
                <a
                  href={managedByLogo.url || '#'}
                  target={managedByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity font-body text-[13px] text-primary-foreground"
                >
                  <RichTextContent
                    path="footer.managed_by_text"
                    defaultValue="<p>Professionally Managed by</p>"
                    as="span"
                    className="text-primary-foreground"
                  />
                  <VibesitesImage
                    url={managedByLogo.image}
                    alt={managedByLogo.alt || 'Property Management Company'}
                    className="h-5 w-auto max-w-[120px] object-contain"
                  />
                </a>
              </EditableSlotGroup>
            )}

            {/* Credits (with labels) + compliance + legal */}
            <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6 w-full">

            {/* Powered by — Multihub (left) */}
            <EditableSlotGroup
              groupPath="footer.powered_by_logo"
              definition={poweredByLogoDef}
              className="flex justify-center md:justify-start"
            >
              <a
                href={poweredByLogo.url || 'https://www.multihub.io/'}
                target={poweredByLogo._target || '_blank'}
                rel="noopener noreferrer"
                aria-label={poweredByLogo.alt || 'Multihub'}
                className="inline-flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity font-body text-[13px] text-primary-foreground"
              >
                <RichTextContent
                  path="footer.powered_by_text"
                  defaultValue="<p>Powered by</p>"
                  as="span"
                  className="text-primary-foreground"
                />
                {useWhiteOnPrimary ? (
                  <MultihubLogoMonoWhite height={16} alt={poweredByLogo.alt || 'Multihub'} />
                ) : (
                  <MultihubLogoMonoBlack height={16} alt={poweredByLogo.alt || 'Multihub'} />
                )}
              </a>
            </EditableSlotGroup>

            {/* Compliance icons + legal nav (center) */}
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center justify-center gap-5 text-[var(--vibe-color-text-on-primary)]/70">
                <FairHousingIcon size={26} color="currentColor" />
                <PetsAllowedIcon size={26} color="currentColor" />
                <WheelchairIcon size={26} color="currentColor" />
              </div>
              <nav className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-body text-[13px] text-primary-foreground" aria-label="Legal links">
                <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                  <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                    {accessibilityLink.text || 'Accessibility'}
                  </a>
                </EditableSlotGroup>
                <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">·</span>
                <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                  <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                    {privacyPolicyLink.text || 'Privacy Policy'}
                  </a>
                </EditableSlotGroup>
                <span aria-hidden="true" className="text-[var(--vibe-color-text-on-primary)]/40">·</span>
                <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                  <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-[var(--vibe-color-text-on-primary)]/60 transition-colors">
                    {sitemapLink.text || 'Sitemap'}
                  </a>
                </EditableSlotGroup>
              </nav>
            </div>

            {/* Website Design by — Repli (right) */}
            <EditableSlotGroup
              groupPath="footer.design_by_logo"
              definition={designByLogoDef}
              className="flex justify-center md:justify-end"
            >
              <a
                href={designByLogo.url || 'https://www.repli360.com/'}
                target={designByLogo._target || '_blank'}
                rel="noopener noreferrer"
                aria-label={designByLogo.alt || 'Repli'}
                className="inline-flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity font-body text-[13px] text-primary-foreground"
              >
                <RichTextContent
                  path="footer.design_by_text"
                  defaultValue="<p>Website Design by</p>"
                  as="span"
                  className="text-primary-foreground"
                />
                <VibesitesImage
                  url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                  alt={designByLogo.alt || 'Repli Logo'}
                  height={22}
                  className="h-[22px] w-auto"
                  priority
                />
              </a>
            </EditableSlotGroup>

            </div>
          </div>
        </div>
      </div>

      {/* ---- Global z-index fallback for the RentCafe IQ launcher ----
          Cheap first pass for the confirmed launcher nodes. The useEffect
          above re-applies the same pin with inline `!important` when the
          widget overrides z-index after mount. */}
      <style>{`
        /* Production RentCafe Connect / IQ launcher (confirmed live DOM):
           floating wrap .rcc-app-cta-wrap, controls .rcc-app-controls,
           bubble #conversations-webchat-toggle. 120 sits just above the
           open header without competing with site modals. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.follow_us_label': {
      label: 'Follow Us Label',
      type: 'richtext',
      defaultValue: '<p>Follow Us</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.copyright_name': {
      label: 'Copyright Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Our Community',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.facebook': facebookLinkDef,
    'footer.instagram': instagramLinkDef,
    'footer.twitter': twitterLinkDef,
    'footer.youtube': youtubeLinkDef,
    'footer.linkedin': linkedinLinkDef,
    'footer.tiktok': tiktokLinkDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
